const ADMIN_MFA_FLAG_KEY = 'rc:pendingAdminOauthMfa';
const ADMIN_MFA_DEST_KEY = 'rc:pendingAdminDest';
const ADMIN_MFA_EMAIL_KEY = 'rc:pendingAdminEmail';
const ADMIN_MFA_USER_ID_KEY = 'rc:pendingAdminUserId';

/** Parámetro de URL distinto de `adminMfa` (forzar challenge): indica que la UI ya está en el paso TOTP interno. */
export const INTERNAL_MFA_UI_QUERY_PARAM = 'rc_internal_mfa_ui';

const safeLocalStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const safeGet = (key: string) => {
  const storage = safeLocalStorage();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string) => {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {}
};

const safeRemove = (key: string) => {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {}
};

export const ADMIN_MFA_QUERY_PARAM = 'adminMfa';
export const INTERNAL_ACCESS_MFA_QUERY_PARAM = ADMIN_MFA_QUERY_PARAM;

export const hasAdminMfaPending = () => safeGet(ADMIN_MFA_FLAG_KEY) === '1';
export const hasInternalAccessMfaPending = hasAdminMfaPending;

export const markAdminMfaPending = (options: {
  dest?: string | null;
  email?: string | null;
  userId?: string | null;
} = {}) => {
  safeSet(ADMIN_MFA_FLAG_KEY, '1');
  if (options.dest) setAdminMfaDest(options.dest);
  if (options.email) setAdminMfaEmail(options.email);
  if (options.userId) safeSet(ADMIN_MFA_USER_ID_KEY, options.userId);
};
export const markInternalAccessMfaPending = markAdminMfaPending;

export const clearAdminMfaState = () => {
  safeRemove(ADMIN_MFA_FLAG_KEY);
  safeRemove(ADMIN_MFA_DEST_KEY);
  safeRemove(ADMIN_MFA_EMAIL_KEY);
  safeRemove(ADMIN_MFA_USER_ID_KEY);
};
export const clearInternalAccessMfaState = clearAdminMfaState;

export const getAdminMfaUserId = () => safeGet(ADMIN_MFA_USER_ID_KEY);
export const getInternalAccessMfaUserId = getAdminMfaUserId;

/** true si la URL indica que el usuario debía seguir en la pantalla de código MFA (sobrevive remount). */
export function readInternalMfaUiFlag(search?: string): boolean {
  if (typeof window === 'undefined') return false;
  const s = search ?? (typeof window.location !== 'undefined' ? window.location.search : '');
  try {
    return new URLSearchParams(s).get(INTERNAL_MFA_UI_QUERY_PARAM) === '1';
  } catch {
    return false;
  }
}

/** Marca o quita el flag de UI MFA en la barra de direcciones sin recargar (no reemplaza `adminMfa=1` de forzado). */
export function replaceInternalMfaUiInAddressBar(on: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    if (on) {
      url.searchParams.set(INTERNAL_MFA_UI_QUERY_PARAM, '1');
    } else {
      url.searchParams.delete(INTERNAL_MFA_UI_QUERY_PARAM);
    }
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // ignore
  }
}

export const getAdminMfaDest = () => safeGet(ADMIN_MFA_DEST_KEY);
export const getInternalAccessMfaDest = getAdminMfaDest;

export const setAdminMfaDest = (dest?: string | null) => {
  if (!dest) {
    safeRemove(ADMIN_MFA_DEST_KEY);
    return;
  }
  safeSet(ADMIN_MFA_DEST_KEY, dest);
};
export const setInternalAccessMfaDest = setAdminMfaDest;

export const getAdminMfaEmail = () => safeGet(ADMIN_MFA_EMAIL_KEY);
export const getInternalAccessMfaEmail = getAdminMfaEmail;

export const setAdminMfaEmail = (email?: string | null) => {
  if (!email) {
    safeRemove(ADMIN_MFA_EMAIL_KEY);
    return;
  }
  safeSet(ADMIN_MFA_EMAIL_KEY, email);
};
export const setInternalAccessMfaEmail = setAdminMfaEmail;
